import { useEffect } from "react"
import { useSelector } from "react-redux"
import { RootState } from "Shared/store/rootState.interface"
import { InboxInvoice, Expense, ExpenseStatusEnum } from "@finway-group/shared/lib/models"
import useStateIfMounted from "Shared/utils/hooks/useStateIfMounted"
import { getExpenseModelByKind, getTableAndAdjacentExpenseLabel } from "Features/pages/expenses/expenseUtils"
import { ExpenseService } from "Shared/services"
import { ExpenseCountInterface } from "Shared/store/actions/expense/expenseTypes"

export const useExpenseCounts = (): ExpenseCountInterface => useSelector(({ expenses }: RootState) => expenses.counts)

export const useInboxInvoicesCount = () => useSelector(({ inboxInvoices }: RootState) => inboxInvoices.count)

export const useShouldRefetchExpenses = () => useSelector(({ expenses }: RootState) => expenses.shouldRefetch)

export const useShouldRefetchInbox = () => useSelector(({ inboxInvoices }: RootState) => inboxInvoices.shouldRefetch)

export const useExpenses = (excludeDeleted: boolean = false) => {
    const { items, totalPages, totalDocs, counts } = useSelector(({ expenses }: RootState) => expenses)
    let expenses = items.map((expense: Expense) => new Expense(expense))

    if (excludeDeleted) {
        expenses = expenses?.filter((expense: Expense) => !expense.deleted)
    }

    return {
        expenses,
        totalPages,
        totalDocs,
        counts,
    }
}

export const useInboxInvoices = () => {
    const { items, totalPages, totalDocs, count } = useSelector(({ inboxInvoices }: RootState) => inboxInvoices)
    const inboxInvoices = items.map((i: InboxInvoice) => new InboxInvoice(i))

    return {
        inboxInvoices,
        totalPages,
        totalDocs,
        count,
    }
}

export const useSubscriptions = (excludeDeleted: boolean = false) => {
    const { expenses } = useExpenses(excludeDeleted)
    return expenses.filter((expense: Expense) => expense.isSubscription() && expense.isInitialSubscription())
}

export const useExpense = () => {
    const expenseState = useSelector(({ expenses }: RootState) => expenses.item)
    const ExpenseModel = getExpenseModelByKind(expenseState.kind)
    return new ExpenseModel(expenseState)
}

export const useActiveTab = () => useSelector(({ expenses }: RootState) => expenses.activeTab)

export const useExpenseSwitching = (expenseId: string, expenseStatus: ExpenseStatusEnum, requestedBy: string) => {
    const [adjacentExpenses, setAdjacentExpenses] = useStateIfMounted({ prevExpense: "", nextExpense: "" })
    const [isLoading, setIsLoading] = useStateIfMounted(false)
    const [hasError, setHasError] = useStateIfMounted(false)
    const [table, adjacentExpenseLabel] = getTableAndAdjacentExpenseLabel(expenseStatus, requestedBy)

    const { prevExpense, nextExpense } = adjacentExpenses

    useEffect(() => {
        setIsLoading(true)
        ExpenseService.getAdjacentExpenses(expenseId, table)
            .then(({ prev, next }: { prev: string; next: string }) => setAdjacentExpenses({ prevExpense: prev, nextExpense: next }))
            .catch(() => setHasError(true))
            .finally(() => setIsLoading(false))
    }, [expenseId])

    return { prevExpense, nextExpense, isExpenseSwitchingLoading: isLoading, hasError, adjacentExpenseLabel }
}
